import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import CustomizedTabs from "../../../shared/components/navigations/tabs";
import ComponentDetails from "./tc-tabs/component-details/index";
import DeclarationByCertificateBody from "./tc-tabs/declaration-by-certificate-body";
import SellerCertifiedProducts from "./tc-tabs/buyer-seller/seller-certfied-products";
import Shipments from "./tc-tabs/shipments/shipments";
import TcStandardWeightInfo from "./tc-tabs/tc-standard-weight-info";
import TransactionCertificate from "./tc-tabs/transaction-cetificate";
import CertifiedProductsComponents from "./tc-tabs/certified-products";
import InputTcProductDetail from "./tc-tabs/input-tc-product-details/index";
import InputTCComponentDetails from "./tc-tabs/input-tc-component-details/index";
import { useParams } from "react-router-dom";
import { useApi } from "../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../shared/app-configs/tc-app-configs";
import { useTransactionCertificateStore } from "../../../store/transaction-certificates.store";
import { Utills } from "../../../shared/app-configs/utilit";
import { dtrackItService } from "../tc-service/dtrackit.service";
/**
 * API returns an object like:
 * {
 *   "transaction-certificate": true,
 *   "seller-buyer-products": true,
 *   "shipments": true,
 *   "certified-products-components": true,
 *   "component-details": true,
 *   "tc-standard-weight": true,
 *   "Input-tc-product-details": true,
 *   "Input-tc-component": true,
 *   "declaration-certification-body": true
 * }
 * where 'true' means "this tab is completed."
 */

const TcForm = () => {
  // Retrieve URL parameters.
  const { formtype, TE_ID } = useParams<{ formtype: string; TE_ID: string }>();
  const baseUrl = formtype ?? "create-tc";
  const system = "tc";
  const enableDisableLogic : Boolean = formtype === 'submited-tc' ? false : true; 

  // We store the API data as an object { [tabId]: boolean }, e.g. { "transaction-certificate": true }
  const [completionMap, setCompletionMap] = useState<Record<string, boolean>>({});

  // Initialize the API hook.
  const { loading, executeGet } = useApi();
 const {getTCStandardGOTS} =useTransactionCertificateStore();
 const { setRawMtrlCodes } = useTransactionCertificateStore();

  /**
   * Fetch the completion data from the API, store it in completionMap,
   * so that 'true' means "that tab is completed."
   */
  const fetchCompletionData = async () => {
    if (!TE_ID) return; // If there's no TE_ID in the route, skip.
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_DISABLE_TABS,
        { tcId: TE_ID }
      );
      console.log("API Response (completionMap):", response);
      // Example response:
      // {
      //   "transaction-certificate": true,
      //   "seller-buyer-products": true,
      //   ...
      // }
      setCompletionMap(response.data || {});
    } catch (error) {
      console.error("Error fetching tab completion status:", error);
    }
  };

  // Fetch completion data immediately on mount or if TE_ID changes.
  useEffect(() => {
    if(enableDisableLogic)
    fetchCompletionData();
  }, [TE_ID]);

  useEffect(() => {
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes()
        const rawMaterials = result.data.map((t: any) => ({ value: t.code_1, label: `${t.description_1} (${t.code_1})` }))
        setRawMtrlCodes(rawMaterials);
      } catch (err) {
        Utills.cl(`Error in Fetching Organic Farm Standard Code ${err}`)
      }
    };
    getRawMaterialCodes()
  }, [])

  /**
   * Called from each tab's onNextTab handler to re-fetch
   * the completion data after saving or moving on.
   */
  const handleNextTab = async () => {
    if(enableDisableLogic)
    await fetchCompletionData();
  };

  /**
   * Helper to see if a given tab is completed (true) in the completionMap.
   * If the tab is not in the map or the map is empty, default to false.
   */
  const isTabCompleted = (tabId: string): boolean => {
    return !!completionMap[tabId];
  };

  // Define which tabs are optional vs. mandatory
  const optionalTabs = ["component-details", "Input-tc-component",...(getTCStandardGOTS ? ["shipments"] : [])];
  const mandatoryTabs = [
    "transaction-certificate",
    "seller-buyer-products",
    ...(!getTCStandardGOTS ? ["shipments"] : []),
    "certified-products-components",
    "tc-standard-weight",
    "Input-tc-product-details",
  ];

  // Build the baseTabs array, passing handleNextTab to tabs that need a "Next" button
  const baseTabs = [
    {
      id: "transaction-certificate",
      label: "Transaction Details",
      component: <TransactionCertificate onNextTab={handleNextTab} />,
    },
    {
      id: "seller-buyer-products",
      label: "Buyer and Seller Details",
      component: <SellerCertifiedProducts onNextTab={handleNextTab} />,
    },
    {
      id: "shipments",
      label: "Shipments",
      component: <Shipments onNextTab={handleNextTab} />,
    },
    {
      id: "certified-products-components",
      label: "Certified Products",
      component: <CertifiedProductsComponents onNextTab={handleNextTab} />,
    },
    {
      id: "component-details",
      label: "Certified Components (Optional)",
      component: <ComponentDetails  />,
    },
    {
      id: "tc-standard-weight",
      label: "Certified Raw Materials and Declared Geographic Origin",
      component: <TcStandardWeightInfo onNextTab={handleNextTab} />,
    },
    {
      id: "Input-tc-product-details",
      label: "Certified Input References",
      component: <InputTcProductDetail onNextTab={handleNextTab} />,
    },
    {
      id: "Input-tc-component",
      label: "Certified Input Components (Optional)",
      component: <InputTCComponentDetails />,
    },
    {
      id: "declaration-certification-body",
      label: "Declarations",
      component: <DeclarationByCertificateBody  onNextTab={handleNextTab} completionMap={completionMap}/>,
    },
  ];

  // Check if TE_ID is present
  const hasTE_ID = Boolean(TE_ID);

  // Compute the final tabs array with dynamic `disabled` logic:
  const tabs = baseTabs.map((tab) => {
    let disabled = false;
    if (!enableDisableLogic) {
      return { ...tab, disabled: false };
    }
    // 1) transaction-certificate is always enabled
    if (tab.id === "transaction-certificate") {
      disabled = false;
    }
    // 2) optional tabs require TE_ID
    else if (optionalTabs.includes(tab.id)) {
      // disabled = !hasTE_ID;
      if (!hasTE_ID) {
        disabled = true;
      } else {
        // Check the previous mandatory tab
        const currentIndex = baseTabs.findIndex(baseTab=>baseTab?.id===tab.id);
        // For the very first mandatory tab, if it's not "transaction-certificate," we can skip
        // but your first mandatory tab is actually "transaction-certificate" so let's handle normal logic
        if (currentIndex> 0) {
          const prevTabId = baseTabs[currentIndex - 1];
          // If the previous mandatory tab is not completed => disable
          if (!isTabCompleted(prevTabId?.id)) {
            disabled = true;
          }
        }
      }
    }
    // 3) declaration is enabled only if TE_ID is present AND all mandatory tabs are completed
    else if (tab.id === "declaration-certification-body") {
      disabled =
        !hasTE_ID ||
        !mandatoryTabs.every((mTabId) => isTabCompleted(mTabId));
    }
    // 4) mandatory tabs (besides the first) require TE_ID and the previous mandatory tab completed
    else if (mandatoryTabs.includes(tab.id) || optionalTabs.includes(tab.id) ) {
      // If we don't have TE_ID, disable
      if (!hasTE_ID) {
        disabled = true;
      } else {
        // Check the previous mandatory tab
        const currentIndex = mandatoryTabs.indexOf(tab.id);
        // For the very first mandatory tab, if it's not "transaction-certificate," we can skip
        // but your first mandatory tab is actually "transaction-certificate" so let's handle normal logic
        if (currentIndex > 0) {
          const prevTabId = mandatoryTabs[currentIndex - 1];
          // If the previous mandatory tab is not completed => disable
          if (!isTabCompleted(prevTabId)) {
            disabled = true;
          }
        }
      }
    }
    // 5) Fallback: if not in mandatoryTabs or optionalTabs (unlikely), require TE_ID
    else {
      disabled = !hasTE_ID;
    }

    return { ...tab, disabled };
  });

  console.log("Computed tabs:",completionMap, tabs);

  return (
    <Box sx={{ mt: "2px" }}>
      <CustomizedTabs 
        tabs={tabs} 
        baseUrl={baseUrl} 
        system={system} 
        ID={TE_ID} 
      />
    </Box>
  );
};

export default TcForm;
